<template>
  <div class="image-gen-block">
    <div class="image-gen-block__grid">
      <div
        v-for="image in chunk.images"
        :key="image.id"
        class="image-gen-block__cell"
        :class="`image-gen-block__cell--${image.status}`"
        :style="{ width: `${cellSize(image).width}px`, height: `${cellSize(image).height}px` }"
      >
        <template v-if="image.status === 'loading'">
          <div class="image-gen-block__shimmer" />
        </template>

        <template v-else-if="image.status === 'finished' && image.url">
          <BaseImage
            :src="image.url"
            :width="cellSize(image).width"
            :height="cellSize(image).height"
            alt="Generated image"
            class="image-gen-block__img"
            @click="openPreview(image)"
          />
        </template>

        <template v-else-if="image.status === 'error'">
          <div class="image-gen-block__error">
            <BbIcon name="warning" size="small" />
            <span>{{ t('common.failed') }}</span>
          </div>
        </template>
      </div>
    </div>

    <div class="image-gen-block__actions" v-if="!showStylePicker">
      <BbBtn
        type="primary"
        size="small"
        class="change-aspect-style-button"
        color="#6222e3"
        :is-disabled="isGenerating || isChangingStyle"
        @click="showStylePicker = true"
      >
        {{ t('conversation.imageParams.changeAspectOrStyle') }}
      </BbBtn>
    </div>
    <ImageGenParamsSSE v-else @submit="onChangeStyle" />

    <BbPopup v-if="selectedImage" v-model="showPreview" width="800px">
      <UserImagePreview
        :image-id="selectedImage.id"
        :in-conversation-context="true"
        :prompt="selectedImage.params?.prompt ?? ''"
        @close="showPreview = false"
        @download="onDownload(selectedImage)"
        @remix="onRemix"
      />
    </BbPopup>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import { SetQueryValue } from '~/constants/mutations';
import { imageCellSize } from '~/utils/format';
import useImageGeneration from '~/composables/useImageGeneration';
import useConversations from '~/composables/useConversations';
import useAssistAgent from '~/composables/useAssistAgent';
import BaseImage from '~/components/common/BaseImage.vue';
import UserImagePreview from '~/components/userImages/UserImagePreview.vue';
import ImageGenParamsSSE from './ImageGenParamsSSE.vue';

export default {
  name: 'ImageGenBlockSSE',
  components: { BaseImage, UserImagePreview, ImageGenParamsSSE },
  props: {
    /**
     * Image gen chunk built by the conversationStore AddChunkSSE mutation.
     * @type {import('vue').PropType<{ type: 'image_gen', images: import('~/store/conversationStore').ImageGenImage[] }>}
     */
    chunk: {
      type: Object,
      required: true,
    },
    messageId: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { t } = useI18n();
    const store = useStore();
    const { downloadUserImage, setUserImageForRemix } = useImageGeneration();
    const { currentConversation, changeImageStyle } = useConversations();
    const { resolveImageGenUrls } = useAssistAgent();

    const showPreview = ref(false);
    const selectedImage = ref(null);
    const showStylePicker = ref(false);
    const isChangingStyle = ref(false);

    const isGenerating = computed(() => props.chunk.images?.some((image) => image.status === 'loading'));

    onMounted(() => {
      const imagesNeedingUrl = (props.chunk.images || []).filter((image) => image.status === 'finished' && !image.url);
      if (!imagesNeedingUrl.length) return;

      resolveImageGenUrls(
        imagesNeedingUrl.map((image) => image.id),
        { messageId: props.messageId, currentConversationId: currentConversation.value?.conversationId },
      );
    });

    const onChangeStyle = async (imageGenerationParams) => {
      showStylePicker.value = false;
      isChangingStyle.value = true;
      try {
        await changeImageStyle('', { ...imageGenerationParams, remixImageId:props.chunk.images[0].id || null });
      } finally {
        isChangingStyle.value = false;
      }
    };

    const openPreview = (image) => {
      selectedImage.value = image;
      showPreview.value = true;
    };

    const onDownload = async (image) => {
      const userConversationId = currentConversation.value?.userId || '';
      await downloadUserImage(image.id, image.name || 'generated-image.png', userConversationId);
    };

    // Prefills the input like GeneratedImageBlock does; sending the query then goes
    // through the commands/goto endpoint (see useConversations#publishToConversation).
    const onRemix = (image) => {
      const userRemixObject = {
        query: image.userQuery,
        remixImage: image.url,
        remixImageId: image.id,
      };
      setUserImageForRemix({ ...image, ...userRemixObject });
      store.dispatch(`conversationStore/${SetQueryValue}`, userRemixObject);
      showPreview.value = false;
    };

    return {
      t,
      showPreview,
      selectedImage,
      showStylePicker,
      isChangingStyle,
      isGenerating,
      cellSize: imageCellSize,
      openPreview,
      onDownload,
      onRemix,
      onChangeStyle,
    };
  },
};
</script>

<style scoped lang="scss">
.image-gen-block {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-3;
}

.image-gen-block__grid {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: $bb-spacing-2;
}

.image-gen-block__actions {
  display: flex;
  justify-content: flex-end;

  .change-aspect-style-button {
    border-radius: 100px;
  }
}

.image-gen-block__cell {
  border-radius: $bb-spacing-2;
  overflow: hidden;
  flex-shrink: 0;
}

.image-gen-block__img {
  cursor: pointer;
}

.image-gen-block__shimmer {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    $bb-neutral-5 25%,
    $bb-neutral-4 50%,
    $bb-neutral-5 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
}

.image-gen-block__error {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $bb-spacing-1;
  background-color: #fee2e2;
  color: $bb-interface-5;
  font-size: 11px;
  font-weight: 500;
}
</style>
